/**
 * Frontend-forced menu paths.
 *
 * Một số trang mới được build ở frontend trước khi backend kịp cấp quyền menu.
 * Với các trang đó, ta "ép" hiển thị cho những user đã có quyền vào nhóm cha
 * tương ứng — tránh phải chờ cấu hình menu phía backend.
 *
 * Pattern giống `mergeOutreachMenuPaths` trong routes/outreach/outreachPaths.js.
 */

// Path của trang SEO Support (đồng bộ với src/menu/index.js & src/routes/index.js).
export const SEO_SUPPORT_PATH = '/app/seo-support';

// Health Solution Library + Library Category (đồng bộ với src/menu/index.js & routes).
export const HEALTH_SOLUTION_PATH = '/app/health-solution';
export const LIBRARY_CATEGORY_PATH = '/app/library-category';

// Seller Interview: trang Seller Interview Flows (đồng bộ với src/menu/index.js & routes).
export const INTERVIEW_QUESTION_PATH = '/app/interview-question';
export const INTERVIEW_FLOW_PATH = '/app/interview-flow';
export const SELLER_PATH = '/app/seller';
export const SELLER_ABOUT_SCORE_PATH = '/app/seller-about-score';
export const PROVIDER_VERIFICATION_PATH = '/app/provider-verification';

// Pro Referral (nhận Pro miễn phí qua giới thiệu) nằm trong nhóm Affiliate.
export const AFFCAMPAIGN_PATH = '/app/affcampaign';
export const PRO_REFERRAL_PATH = '/app/pro-referral';

// Languages nằm cạnh Countries trong nhóm Settings (đồng bộ với src/menu/index.js & routes).
export const COUNTRIES_PATH = '/app/countries';
export const LANGUAGES_PATH = '/app/languages';

// Public Offers nằm trong nhóm Request (đồng bộ với src/menu/index.js & routes).
export const REQUESTS_PATH = '/app/requests';
export const PUBLIC_OFFERS_PATH = '/app/public-offers';

// Các trang mới thuộc nhóm "Contents" cần ép hiển thị cho user có quyền Contents.
const FORCED_CONTENT_PATHS = [SEO_SUPPORT_PATH, HEALTH_SOLUTION_PATH, LIBRARY_CATEGORY_PATH];

// Các path con hiện có của nhóm "Contents" (đồng bộ với khối `key: 'content'`
// trong src/menu/index.js). Dùng để xác định user có quyền vào Contents hay không.
const CONTENT_CHILD_PATHS = [
  '/app/posts',
  '/app/partner-recruitment-page',
  '/app/testimonials',
  '/app/wellness-test',
  '/app/faq',
  '/app/templates',
  '/app/brevo-contact',
  '/app/widgets',
  '/app/ldp-inquiries',
  '/app/ldp-inquiry-send-emails',
  '/app/landing-page-search',
  HEALTH_SOLUTION_PATH,
  LIBRARY_CATEGORY_PATH,
];

/**
 * Ép hiển thị các trang Contents mới (SEO Support, Health Solution, Library
 * Category) NẾU user đã có quyền vào Contents (có ít nhất 1 path con của
 * Contents). Nếu không có quyền Contents thì giữ nguyên — không làm lộ trang
 * cho các role không liên quan.
 */
export function mergeContentExtraMenuPaths(menuPaths) {
  const paths = Array.isArray(menuPaths) ? [...menuPaths] : [];

  const hasContentAccess = paths.some(
    (p) => typeof p === 'string' && CONTENT_CHILD_PATHS.includes(p)
  );
  // User có quyền Interview Questions thì cũng được vào Seller Interview Flows
  // (cùng quản lý bộ câu hỏi), khỏi chờ backend cấp quyền menu mới.
  if (paths.includes(INTERVIEW_QUESTION_PATH)) {
    paths.push(INTERVIEW_FLOW_PATH);
  }
  // Ai quản lý được danh sách Seller thì vào được trang AI chấm About của seller,
  // khỏi chờ khai báo menu mới trong Menu Manager. Tương tự trang Provider verification.
  if (paths.includes(SELLER_PATH)) {
    paths.push(SELLER_ABOUT_SCORE_PATH, PROVIDER_VERIFICATION_PATH);
  }
  // Ai quản lý được Affiliate campaign thì vào được trang Pro Referral (duyệt
  // yêu cầu nhận Pro của campaign 64), khỏi chờ khai báo menu mới trong Menu Manager.
  if (paths.includes(AFFCAMPAIGN_PATH) || paths.includes(SELLER_PATH)) {
    paths.push(PRO_REFERRAL_PATH);
  }
  // Ai quản lý được Requests thì quản lý được Public Offers (trang con của
  // nhóm Request), khỏi chờ khai báo menu mới trong Menu Manager.
  if (paths.includes(REQUESTS_PATH)) {
    paths.push(PUBLIC_OFFERS_PATH);
  }

  // Ai quản lý được Countries thì quản lý được Languages, khỏi chờ khai báo
  // menu mới trong Menu Manager.
  if (paths.includes(COUNTRIES_PATH)) {
    paths.push(LANGUAGES_PATH);
  }

  if (!hasContentAccess) return [...new Set(paths)];

  return [...new Set([...paths, ...FORCED_CONTENT_PATHS])];
}
